import { useState, useEffect, useMemo } from 'react';
import { useStateSelector } from '../store/selectors';
import { useDispatch } from 'react-redux';
import { FetchEngagementConferences } from '../slices/marketplace/meetings/thunks/fetch-engagement-conferences.thunk';
import { useCurrentMarketplaceParty } from './useCurrentMarketplaceParty';
import { TimelineParty } from '../slices/marketplace/timeline/models/enums/timeline-party.enum';

export const useHasMeetingsOrClips = (engagementId: number): boolean => {
    const [hasClips, setHasClips] = useState(false);
    const axios = useStateSelector((state) => state.core.axios);
    const dispatch = useDispatch() as any;
    const engagementMeetingsState = useStateSelector(
        (state: any) => state.engagementMeetings
    );
    const selectedOpportunity = useStateSelector(
        (state: any) => state.opportunities?.selectedOpportuity
    );
    const party = useCurrentMarketplaceParty(engagementId);
    
    const hasMeetings = useMemo(() => {
        if (!engagementMeetingsState?.engagementMeetings) {
            return false;
        }
        const meetingsForEngagement = engagementMeetingsState.engagementMeetings.filter(
            (meeting: any) => meeting.engagementId === engagementId
        );
        return meetingsForEngagement.length > 0;
    }, [engagementMeetingsState?.engagementMeetings, engagementId]);
    
    const isBuyerPartOfEngagement = useMemo(() => {
        if (party !== TimelineParty.Buyer) {
            return true; 
        }

        if (selectedOpportunity && selectedOpportunity.opportunityId === engagementId) {
            if (!selectedOpportunity?.buyer) {
                return false;
            }
            if (!selectedOpportunity.buyer.isActive) {
                return false;
            }
            return true;
        }
        
        return true;
    }, [party, selectedOpportunity, engagementId]);
    
    // Reset hasClips when engagementId changes
    useEffect(() => {
        setHasClips(false);
    }, [engagementId]);
    
    useEffect(() => {
        if (!isBuyerPartOfEngagement) {
            return;
        }
        
        // Always fetch meetings when engagementId changes to ensure we have the latest data
        if (engagementId) {
            dispatch(FetchEngagementConferences(engagementId));
        }
    }, [engagementId, dispatch, isBuyerPartOfEngagement]);
    
    useEffect(() => {
        if (!isBuyerPartOfEngagement) {
            setHasClips(false);
            return;
        }
        
        const checkClips = async () => {
            if (!engagementId) {
                return;
            }

            try {
                const myClipsResponse = await axios.get(
                    `/api/Marketplace/Engagements/${engagementId}/EngagementMeetings/clips`
                );
                
                if (myClipsResponse.status === 200 && myClipsResponse.data?.length > 0) {
                    setHasClips(true);
                    return;
                }
                
                const sharedClipsResponse = await axios.get(
                    `/api/Marketplace/Engagements/${engagementId}/EngagementMeetings/clips/shared`
                );
                
                if (sharedClipsResponse.status === 200 && sharedClipsResponse.data?.length > 0) {
                    setHasClips(true);
                } else {
                    setHasClips(false);
                }
            } catch (error) {
                setHasClips(false);
            }
        };
        
        checkClips();
    }, [engagementId, axios, isBuyerPartOfEngagement]);

    if (!isBuyerPartOfEngagement) {
        return false;
    }
    
    return hasMeetings || hasClips;
};